import { useEffect, useMemo } from "react";

import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import * as Sentry from "@sentry/react";
import { compact, map } from "lodash/fp";
import * as yup from "yup";

import Button from "@readai/common/components/Button";
import useAuth from "@readai/common/hooks/useAuth";
import usePagination from "@readai/common/hooks/usePagination";
import { withQueryParams } from "@readai/common/utils";
import yupResolver from "@readai/common/utils/yupResolver";

import useRssLinkCalendarEvent from "@app/booking/hooks/useRssLinkCalendarEvent";
import useScheduleEvent from "@app/booking/hooks/useScheduleEvent";
import ROUTES from "@app/booking/routes";
import useSchedulingStore from "@app/booking/stores/scheduling";
import * as Events from "@app/booking/tracking/events";
import type {
  PublicRssLink,
  RssLinkCalendarEventCreateBody,
} from "@app/booking/types/rssLinkTypes";

import ChooseDuration from "./ChooseDuration";
import MeetingDetails, { MeetingDetailsFieldValues } from "./MeetingDetails";
import SelectDateAndTime from "./SelectDateAndTime";
import { Content, Footer } from "./styles";

const CONSTRAINED_LINK_STEPS = ["date-and-time", "details"];
const GENERAL_LINK_STEPS = ["duration", ...CONSTRAINED_LINK_STEPS];

const getSchedulingValidationSchema = () =>
  yup.object().shape({
    schedulerName: yup.string().required(t`Your name is required to continue`),
    schedulerEmail: yup
      .string()
      .required(t`Your email is required to continue`)
      .email(t`Must be a valid email`),
    guestEmails: yup.array().of(
      yup.object().shape({
        value: yup
          .string()
          .email(t`Must be a valid email`)
          .required(t`An email is required`),
      }),
    ),
    details: yup.string(),
    platform: yup.string().required(t`Please select a conferencing platform`),
  });

interface ScheduleEventContentProps {
  allLinks: PublicRssLink[];
  autoAdvance: boolean;
  ownerName?: string;
  ownerVanityId?: string;
  linkId?: string;
}

const ScheduleEventContent = ({
  allLinks,
  autoAdvance,
  ownerName,
  ownerVanityId,
  linkId,
}: ScheduleEventContentProps) => {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useSearchParams();
  const eventId = queryParams.get("eventId");

  const { user } = useAuth();

  const {
    getEvent: { data: existingEvent },
  } = useRssLinkCalendarEvent(eventId);

  const { scheduleEvent, rescheduleEvent } = useScheduleEvent();

  const {
    allowedPlatforms,
    duration,
    selectedTime,
    rssLinkVanityId,
    resetSelections,
  } = useSchedulingStore((state) => ({
    allowedPlatforms: state.allowedPlatforms,
    duration: state.constraints?.allowedDuration ?? null,
    selectedTime: state.selectedTime,
    rssLinkVanityId: state.constraints?.vanityId,
    resetSelections: state.resetSelections,
  }));

  const isDurationConstrainedLink = Boolean(linkId);

  const steps =
    isDurationConstrainedLink || existingEvent
      ? CONSTRAINED_LINK_STEPS
      : GENERAL_LINK_STEPS;

  const selectedStep = queryParams.get("step") ?? steps[0];
  const selectedStepIndex = steps.indexOf(selectedStep);

  const { currentPage, ...pagination } = usePagination(steps, {
    pageSize: 1,
    initialPage: selectedStepIndex,
  });

  const validationSchema = useMemo(getSchedulingValidationSchema, [
    i18n.locale,
  ]);

  const form = useForm<MeetingDetailsFieldValues>({
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
    defaultValues: {
      schedulerName: user ? user.fullName : "",
      schedulerEmail: user ? user.email : "",
      details: existingEvent ? existingEvent.details : "",
      platform: allowedPlatforms[0]?.value ?? "",
    },
  });

  useEffect(() => {
    if (!existingEvent) return;
    form.setValue("platform", existingEvent.meetingPlatform);
  }, [existingEvent]);

  useEffect(() => {
    setQueryParams((params) => {
      params.set("step", steps[currentPage]);
      return params;
    });
  }, [currentPage, steps.length]);

  const requireDurationSelect =
    !isDurationConstrainedLink && !duration && !eventId;

  const requireDateAndTimeSelect = isDurationConstrainedLink && !selectedTime;

  useEffect(() => {
    if (
      (requireDurationSelect && selectedStep !== "duration") ||
      (requireDateAndTimeSelect && selectedStep !== "date-and-time")
    ) {
      pagination.goToPage(0);
    }
  }, [selectedStep, requireDurationSelect, requireDateAndTimeSelect]);

  const resetState = () => {
    resetSelections();
    form.reset();
  };

  const handleScheduleEventError = (error: unknown) => {
    Sentry.captureException(error);
    Events.scheduleEventError();
  };

  const isContinueDisabled =
    selectedStep === "duration"
      ? !duration
      : selectedStep === "date-and-time"
        ? !selectedTime
        : selectedStep === "details"
          ? !form.formState.isValid
          : true;

  const handleScheduleEvent = (data: MeetingDetailsFieldValues) => {
    // the user can't get to this method w/o these fields being populated, but
    // early return to appease the types
    if (!selectedTime || !duration || !ownerVanityId || !rssLinkVanityId) {
      return;
    }

    const compactGuestEmails = compact(map("value", data.guestEmails));
    const body: RssLinkCalendarEventCreateBody = {
      startTime: selectedTime,
      endTime: selectedTime.plus({ minutes: duration }),
      schedulerName: data.schedulerName,
      schedulerEmail: data.schedulerEmail,
      additionalGuestsEmails: compactGuestEmails,
      details: data.details,
      meetingPlatform: data.platform,
      rssUserVanityId: ownerVanityId,
      rssLinkVanityId,
    };

    if (existingEvent) {
      Events.rescheduleEvent();
      rescheduleEvent.mutate(
        { eventId: existingEvent.id, patchBody: body },
        {
          onSuccess: resetState,
          onError: handleScheduleEventError,
        },
      );
    } else {
      Events.scheduleEvent({ platform: data.platform });
      scheduleEvent.mutate(body, {
        onSuccess: resetState,
        onError: handleScheduleEventError,
      });
    }
  };

  const handleClickContinue = (linkVanityId?: string) => {
    if (selectedStep === "duration") {
      navigate(
        withQueryParams(
          ROUTES.scheduleByVanityIds(
            ownerVanityId!,
            linkVanityId ?? rssLinkVanityId,
          ),
          { step: "date-and-time" },
        ),
      );
    } else {
      pagination.goToNextPage();
    }
  };

  const handleClickScheduleEvent = form.handleSubmit(handleScheduleEvent);

  const handleAutoAdvance = autoAdvance ? handleClickContinue : undefined;
  const handleAutoAdvanceBack =
    autoAdvance && pagination.hasPrevious
      ? pagination.goToPreviousPage
      : undefined;

  const isSubmitLoading = scheduleEvent.isPending || rescheduleEvent.isPending;

  const submitButton = (
    <Button
      disabled={isContinueDisabled}
      type="submit"
      loading={isSubmitLoading}
      onClick={handleClickScheduleEvent}
      sx={{ width: { xxs: "100%", lg: "fit-content" }, maxWidth: "500px" }}
    >
      {existingEvent ? (
        <Trans>Reschedule event</Trans>
      ) : (
        <Trans>Schedule event 🎉</Trans>
      )}
    </Button>
  );

  return (
    <>
      <Content>
        {selectedStep === "duration" ? (
          <ChooseDuration links={allLinks} onContinue={handleAutoAdvance} />
        ) : selectedStep === "date-and-time" ? (
          <SelectDateAndTime
            onContinue={handleAutoAdvance}
            ownerName={ownerName}
          />
        ) : selectedStep === "details" ? (
          <MeetingDetails
            form={form}
            onBack={handleAutoAdvanceBack}
            submitButton={autoAdvance ? submitButton : null}
          />
        ) : null}
      </Content>
      <Footer>
        {pagination.hasPrevious && (
          <Button variant="text" onClick={pagination.goToPreviousPage}>
            <Trans>Back</Trans>
          </Button>
        )}
        {pagination.hasNext ? (
          <Button
            onClick={() => handleClickContinue()}
            disabled={isContinueDisabled}
          >
            <Trans>Continue</Trans>
          </Button>
        ) : (
          submitButton
        )}
      </Footer>
    </>
  );
};

export default ScheduleEventContent;
